import {
  PostCollectionInfoV2Fragment,
  PostInfoV2Fragment,
  PostInfoV2NonRecursiveFragment,
  PostInfoV2WithDerivativesFragment,
  TawkInfoV2Fragment,
} from "../API";

import {
  NonRecursivePostCardConfig,
  NonRecursivePostCardPrimaryContnetConfig,
} from "../Components/NonRecursivePostCard/NonRecursivePostCard";

import { PostCardConfig } from "../Components/PostCard/PostCard";

import { TawkPillConfig } from "../Components/TawkPill/TawkPill";
import {
  InlinePlayableTawkPillConfig,
  EchoTimeRange,
} from "../Components/InlinePlayableTawkPill/InlinePlayableTawkPill";
import { EchoConfig } from "../Components/Echo/Echo";
import { TopAnnotationConfig } from "../Components/TopAnnotation/TopAnnotation";
import { TextBubbleConfig } from "../Components/TextBubble/TextBubble";
import { InteractionBarConfig } from "../Components/InteractionBar/InteractionBar";

import { tawksColorsWithCharacteristicHue } from "./TawkitColorsProvider";
import { timeAgoDescription, durationDescription } from "./DateManipulation";
import { FootNoteConfig } from "../Features/ExpandedPost/ExpandedPostHeader";
import { PostNavigatingTawkPillConfig } from "../Components/PostNavigatingTawkPill/PostNavigatingTawkPill";
import {
  PlayableEntity,
  authorlikeConfig,
  playablePaylaod,
} from "./PlayableEntity";
import { PlaylistCardConfig } from "../Components/PlaylistCard/PlaylistCard";
import { notEmpty } from "./MissingStd";

/* eslint-disable no-bitwise */
function characteristicHueFallback(post: PostInfoV2Fragment): number {
  const str = post.associatedTawk?.title ?? "";

  let sum = 0;
  for (let i = 0; i < str.length; i += 1) {
    sum += ((i + 1) * (str.codePointAt(i) ?? 0)) / (1 << 8);
  }
  return sum % 1;
}
/* eslint-enable no-bitwise */

// TODO: move to dedicated URL generator
function postUrlPath(post: PostInfoV2NonRecursiveFragment): string {
  const authorHandle = post.author.userHandle?.renderedForm ?? "anon";
  const postId = post.id;

  return `/@${authorHandle}/${postId}`;
}

function makeInteractionBarConfig(
  post: PostInfoV2NonRecursiveFragment
): InteractionBarConfig {
  return {
    plays: 0,
    echoes: 0,
    comments: post.repliesCount,
    likes: post.likesCount,
    associatedPostHref: postUrlPath(post),
  };
}

function makeTawkPillConfig(
  playableEntity: PlayableEntity,
  characteristicHue: number,
  clippedTimeRange: EchoTimeRange | null
): TawkPillConfig {
  const relevantDuration = (function _duration() {
    if (clippedTimeRange) {
      return clippedTimeRange.endTime - clippedTimeRange.startTime;
    }
    return playableEntity.duration ?? 320; // TODO: emit a logging event if duration is null. old tawks do not have duration, but new ones do.
  })();

  const isClip = clippedTimeRange != null;

  const playableEntityAuthorlikeConfig = authorlikeConfig(playableEntity);

  return {
    tawkTitle: playableEntity.title ?? "untitled",
    authorProfilePicConfig: playableEntityAuthorlikeConfig.profilePic,
    authorUserHandle: playableEntityAuthorlikeConfig.userHandleLike,
    colorsProvider: tawksColorsWithCharacteristicHue(characteristicHue),
    durationDescription: durationDescription(relevantDuration),
    isClip,
    size: isClip ? "normal" : "normal",
  };
}

export function makePostNavigatingTawkPillConfig(
  postInfoV2: PostInfoV2Fragment
): PostNavigatingTawkPillConfig | null {
  if (postInfoV2.associatedTawk) {
    return {
      tawkPillConfig: makeTawkPillConfig(
        postInfoV2.associatedTawk,
        postInfoV2.associatedColorHue ?? characteristicHueFallback(postInfoV2),
        postInfoV2.repostedTimeRange ?? null
      ),
      postId: postInfoV2.id,
    };
  }
  return null;
}

function makeInlinePlayableTawkPillConfig(
  playableEntity: PlayableEntity,
  characteristicHue: number,
  clippedTimeRange: EchoTimeRange | null,
  preExpandedAndAutoplaying?: boolean
): InlinePlayableTawkPillConfig {
  return {
    kind: "InlinePlayableTawkPillConfig",
    tawkPillConfig: makeTawkPillConfig(
      playableEntity,
      characteristicHue,
      clippedTimeRange
    ),
    playablePayload: playablePaylaod(playableEntity),
    timeRange: clippedTimeRange,
    preExpandedAndAutoplaying,
  };
}

function makeTopAnnotationConfig(
  postInfoV2: PostInfoV2NonRecursiveFragment
): TopAnnotationConfig {
  return {
    userName: postInfoV2.author.name ?? "Anon",
    userHandle: postInfoV2.author.userHandle?.renderedForm ?? "anon",
    timeAgoDescription: timeAgoDescription(postInfoV2.createdAt),
  };
}
export function makePrimaryContentConfig(
  postInfoV2: PostInfoV2Fragment,
  preExpandedAndAutoplaying?: boolean
): NonRecursivePostCardPrimaryContnetConfig {
  const maybeEchoedPost = postInfoV2.repostedPost;
  const maybeEchoedPlayableEntity: PlayableEntity | null =
    maybeEchoedPost?.associatedTawk ??
    maybeEchoedPost?.associatedExternalTawk ??
    null;

  if (maybeEchoedPost && maybeEchoedPlayableEntity) {
    const echoedPlayableEntity: PlayableEntity = maybeEchoedPlayableEntity;
    const echoedPost: PostInfoV2NonRecursiveFragment = maybeEchoedPost;
    const echoedTimeRange = postInfoV2.repostedTimeRange ?? null;

    const echoConfig: EchoConfig = {
      kind: "EchoConfig",
      echoText: postInfoV2.text,
      authorProfilePicId: postInfoV2.author.profilePhoto.publicId,
      topAnnotationConfig: makeTopAnnotationConfig(echoedPost),
      tawkPillConfig: makeInlinePlayableTawkPillConfig(
        echoedPlayableEntity,
        echoedPost.associatedColorHue ?? characteristicHueFallback(postInfoV2),
        echoedTimeRange,
        preExpandedAndAutoplaying
      ),
      textColorsProvider: tawksColorsWithCharacteristicHue(
        echoedPost.associatedColorHue ?? characteristicHueFallback(postInfoV2)
      ),
    };

    return echoConfig;
  }

  const maybeTawk = postInfoV2.associatedTawk;
  if (maybeTawk) {
    const tawk: TawkInfoV2Fragment = maybeTawk;
    return makeInlinePlayableTawkPillConfig(
      tawk,
      postInfoV2.associatedColorHue ?? characteristicHueFallback(postInfoV2),
      null,
      preExpandedAndAutoplaying
    );
  }

  const replyConfig: TextBubbleConfig = {
    kind: "TextBubbleConfig",
    textBubbleText: postInfoV2.text,
    authorUserHandle: postInfoV2.author.userHandle?.renderedForm ?? "unknown",
    authorProfilePic: {
      kind: "CloudinaryImageConfig",
      publicId: postInfoV2.author.profilePhoto.publicId,
    },
  };

  return replyConfig;
}

export function makeNonRecursivePostCardConfig(
  postInfoV2: PostInfoV2Fragment,
  preExpandedAndAutoplaying?: boolean
): NonRecursivePostCardConfig {
  return {
    interactionBarConfig: makeInteractionBarConfig(postInfoV2),
    primaryContentConfig: makePrimaryContentConfig(
      postInfoV2,
      preExpandedAndAutoplaying
    ),
    topAnnotationBarConfig: makeTopAnnotationConfig(postInfoV2),
    analyticsParams: {
      /* eslint-disable @typescript-eslint/naming-convention */
      post_id: postInfoV2.id,
      post_text: postInfoV2.text,
      post_associatedTawk_ownerSub: postInfoV2.associatedTawk?.ownerSub,
      post_associatedTawk_localId: postInfoV2.associatedTawk?.localId,
      post_associatedTawk_tawkVersion: postInfoV2.associatedTawk?.tawkVersion,
      post_likes_count: postInfoV2.likesCount,
      post_replies_count: postInfoV2.repliesCount,
      post_associatedExternalTawk_id: postInfoV2.associatedExternalTawk?.id,
      post_associatedExternalTawk_originName:
        postInfoV2.associatedExternalTawk?.originName,
      post_associatedExternalTawk_originKind:
        postInfoV2.associatedExternalTawk?.originKind,
      post_associatedExternalTawk_originUrl:
        postInfoV2.associatedExternalTawk?.originUrl,
      post_author_sub: postInfoV2.author.sub,
      post_author_handle: postInfoV2.author.userHandle?.renderedForm,
      post_author_name: postInfoV2.author.name,
    },
    /* eslint-enable @typescript-eslint/naming-convention */
  };
}

export function makePostCardConfig(
  postInfoV2: PostInfoV2WithDerivativesFragment
): PostCardConfig {
  const replies = (postInfoV2.replies?.items ?? [])
    .filter(notEmpty)
    .map((postInfo) => makeNonRecursivePostCardConfig(postInfo));

  const echoes = (postInfoV2.reposts?.items ?? [])
    .filter(notEmpty)
    .map((postInfo) => makeNonRecursivePostCardConfig(postInfo));

  return {
    primaryPostConfig: makeNonRecursivePostCardConfig(postInfoV2, false),
    derivativePostConfigs: replies.concat(echoes),
    analyticsParams: {},
  };
}

export function makeFootnoteConfig(
  postInfoV2: PostInfoV2NonRecursiveFragment
): FootNoteConfig | null {
  const { footnote } = postInfoV2;
  if (footnote) {
    return {
      footnote,
    };
  }

  return null;
}

export function makePlaylistCardConfig(
  playlistInfo: PostCollectionInfoV2Fragment
): PlaylistCardConfig {
  return {
    playlistId: playlistInfo.id,
    playlistName: playlistInfo.name,
    playlistDescription: playlistInfo.description,
    playlistAuthorUserHandle:
      playlistInfo.author.userHandle?.renderedForm ?? "Unknown",
    playlistAuthorProfilePic: {
      kind: "CloudinaryImageConfig",
      publicId: playlistInfo.author.profilePhoto.publicId,
    },
  };
}
